import React, { useState, useEffect, useCallback } from 'react';
import { Modal, Card, Spin, message, Button, Input, Select, Empty, Divider, Tag, Radio, Space, Alert } from 'antd';
import { SearchOutlined, UserOutlined, CheckOutlined, ClockCircleOutlined, EnvironmentOutlined, InfoCircleOutlined, CalendarOutlined } from '@ant-design/icons';
import { FaUserMd, FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import { FaRegCalendarDays } from 'react-icons/fa6';
import drEasyApi from '../../../Api/Customapi';
import { getImg } from '../../../Api/getImg';
import dayjs from 'dayjs';

const ConsultantAssignmentModal = ({
  visible,
  onCancel,
  onAssign,
  session,
  isMobile,
}) => {
  const [consultants, setConsultants] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedConsultant, setSelectedConsultant] = useState(null);
  const [assigningId, setAssigningId] = useState(null);
  const [filterWorkingDay, setFilterWorkingDay] = useState(null);
  const [selectedClinic, setSelectedClinic] = useState(null);
  const [selectedWorkingDay, setSelectedWorkingDay] = useState(null);
  const [selectedAvailabilityHour, setSelectedAvailabilityHour] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize] = useState(20);
  const [totalCount, setTotalCount] = useState(0);
  const [selectedDate, setSelectedDate] = useState(null);
  const [currentMonth, setCurrentMonth] = useState(dayjs());
  const [dateError, setDateError] = useState('');
  const [notes, setNotes] = useState('');
  const [validationErr, setValidationErr] = useState('');

  const workingDays = [
    { value: 0, label: 'Sunday' },
    { value: 1, label: 'Monday' },
    { value: 2, label: 'Tuesday' },
    { value: 3, label: 'Wednesday' },
    { value: 4, label: 'Thursday' },
    { value: 5, label: 'Friday' },
    { value: 6, label: 'Saturday' },
  ];

  // Fetch consultants (doctors) - FIXED to match specialist pattern
  const fetchConsultants = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        PageNumber: pageNumber,
        PageSize: pageSize,
      };

      // Send search term to API instead of filtering client-side
      if (searchTerm.trim()) {
        params.NameSearch = searchTerm;
      }

      // Send working day filter to API instead of filtering client-side
      if (filterWorkingDay !== null && filterWorkingDay !== undefined) {
        params.WorkingDay = filterWorkingDay;
      }

      const response = await drEasyApi.get('/api/Centers/getMyDoctors', {
        params,
      });

      setConsultants(response.data?.doctors || []);
      setTotalCount(response.data?.totalCount || 0);
    } catch (error) {
      console.error('Error fetching consultants:', error);
      message.error('Failed to fetch consultants');
      setConsultants([]);
    } finally {
      setLoading(false);
    }
  }, [pageNumber, pageSize, searchTerm, filterWorkingDay]);

  useEffect(() => {
    if (visible) {
      setPageNumber(1);
      setSearchTerm('');
      setFilterWorkingDay(null);
      resetSelection();
    }
  }, [visible]);

  useEffect(() => {
    if (visible) {
      fetchConsultants();
    }
  }, [visible, fetchConsultants]);

  const resetSelection = () => {
    setSelectedConsultant(null);
    setSelectedClinic(null);
    setSelectedWorkingDay(null);
    setSelectedAvailabilityHour(null);
    setSelectedDate(null);
    setDateError('');
    setNotes('');
  };

  const handleSearch = useCallback((value) => {
    setSearchTerm(value);
    setPageNumber(1);
  }, []);

  const handleWorkingDayChange = useCallback((value) => {
    setFilterWorkingDay(value);
    setPageNumber(1);
  }, []);

  const handleConsultantSelect = async (consultant) => {
    if (selectedConsultant?.doctorId === consultant.doctorId) {
      resetSelection();
    } else {
      // Fetch full doctor details with clinics
      try {
        setLoading(true);
        const response = await drEasyApi.get(`/api/Doctor/GetDoctorBySlug/${consultant.slug}`);

        // Transform the API response to match the expected structure
        const doctorWithClinics = {
          ...consultant,
          clinics: response.data?.clinics?.map(clinic => ({
            clinicId: clinic.id,
            clinicName: clinic.name,
            detailedAddress: clinic.detailedAddress,
            contactPhone: clinic.contactPhone,
            contactEmail: clinic.contactEmail,
            workingDays: clinic.workingDays?.map(wd => ({
              workingDayId: wd.id,
              dayOfWeek: wd.workingDay,
              isWorking: wd.isWorking,
              availabilityHours: wd.availabilityHours?.map(ah => ({
                availabilityHourId: ah.id,
                startTime: ah.startTime,
                endTime: ah.endTime,
                isHourly: ah.isHourly,
                maxPatients: ah.maxPatients,
                maxPatientType: ah.maxPatientType
              })) || []
            })) || []
          })) || []
        };

        setSelectedConsultant(doctorWithClinics);
        setSelectedClinic(null);
        setSelectedWorkingDay(null);
        setSelectedAvailabilityHour(null);
        setSelectedDate(null);
        setDateError('');
      } catch (error) {
        console.error('Error fetching consultant details:', error);
        message.error('Failed to fetch consultant details');
      } finally {
        setLoading(false);
      }
    }
  };

  const handleClinicSelect = (clinic) => {
    setSelectedClinic(clinic);
    setSelectedWorkingDay(null);
    setSelectedAvailabilityHour(null);
    setSelectedDate(null);
    setDateError('');
  };

  const handleWorkingDaySelect = (workingDay) => {
    setSelectedWorkingDay(workingDay);
    setSelectedAvailabilityHour(null);
  };

  const handleAvailabilityHourSelect = (hour) => {
    setSelectedAvailabilityHour(hour);
  };

  // Date picker functions
  const getAvailableWorkingDays = () => {
    if (!selectedClinic) return [];
    return selectedClinic.workingDays?.filter(wd => wd.isWorking) || [];
  };

  const isWorkingDay = (date) => {
    const availableWorkingDays = getAvailableWorkingDays();
    const dayOfWeek = date.day();
    return availableWorkingDays.some(wd => wd.dayOfWeek === dayOfWeek);
  };

  const disabledDate = (current) => {
    if (current && current < dayjs().startOf('day')) {
      return true;
    }
    return !isWorkingDay(current);
  };

  const handleDateSelect = (date) => {
    if (disabledDate(date)) {
      setDateError('This date is not available for the selected consultant');
      return;
    }

    setSelectedDate(date);
    setDateError('');
  };

  const handleAssign = async () => {
    if (!selectedConsultant) {
      message.warning('Please select a consultant');
      return;
    }

    if (!selectedAvailabilityHour) {
      message.warning('Please select an availability hour');
      return;
    }

    if (!selectedDate) {
      message.warning('Please select a booking date');
      return;
    }
    if (notes.trim().length === 0) {
      setValidationErr('Notes are required'); return;
    }
    setAssigningId(selectedConsultant.doctorId);
    try {
      // Prepare the booking request
      const bookingRequest = {
        availabilityHourId: selectedAvailabilityHour.availabilityHourId,
        bookingDate: selectedDate.format('YYYY-MM-DD'),
        userId: session?.userId || session?.patientId,
        notes: notes || undefined,
        selectedHour: null
      };

      const res = await drEasyApi.post('/api/Book/Book', bookingRequest);

      message.success(
        `Successfully assigned ${session?.patientName} to Dr. ${selectedConsultant.name}`
      );
      await drEasyApi.put('/api/Book/AssignExistBookingToDoctor', { doctorId: selectedConsultant.doctorId, bookingId: res?.data?.id });
      onAssign(selectedConsultant);
      handleCancel();
    } catch (error) {
      console.error('Error assigning consultant:', error);
      message.error(
        error.response?.data[0] ||
        'Failed to assign consultant. Please try again.'
      );
    } finally {
      setAssigningId(null);
    }
  };

  const handleCancel = () => {
    resetSelection();
    setSearchTerm('');
    setValidationErr('');
    setFilterWorkingDay(null);
    setPageNumber(1);
    onCancel();
  };

  // Get available clinics for selected consultant
  const availableClinics = selectedConsultant?.clinics || [];

  // Get working days for selected clinic
  const availableWorkingDays = selectedClinic?.workingDays?.filter(wd => wd.isWorking) || [];

  // Get availability hours for selected working day
  const availabilityHours = selectedWorkingDay?.availabilityHours || [];

  // Generate calendar days for the current month
  const startOfMonth = currentMonth.startOf('month');
  const endOfMonth = currentMonth.endOf('month');
  const startDay = startOfMonth.day();
  const daysInMonth = endOfMonth.date();
  const days = [];

  // Add empty slots for days before the 1st of the month
  for (let i = 0; i < startDay; i++) {
    days.push(null);
  }

  // Add actual days of the month
  for (let i = 1; i <= daysInMonth; i++) {
    const date = startOfMonth.set('date', i);
    days.push(date);
  }

  // Add empty slots after the last day to fill the grid
  const remainingSlots = 42 - days.length;
  for (let i = 0; i < remainingSlots; i++) {
    days.push(null);
  }

  return (
    <Modal
      title={
        <div className="flex items-center">
          <div
            className={`${isMobile ? 'w-6 h-6' : 'w-8 h-8'
              } bg-primary rounded-full flex items-center justify-center ${isMobile ? 'mr-2' : 'mr-3'
              }`}
          >
            <FaUserMd className={`text-white ${isMobile ? 'text-xs' : 'text-sm'}`} />
          </div>
          <span className={isMobile ? 'text-sm' : ''}>
            Assign Consultant to {session?.patientName}
          </span>
        </div>
      }
      open={visible}
      onCancel={handleCancel}
      width={isMobile ? '95vw' : 900}
      centered={isMobile}
      className="consultant-assignment-modal"
      footer={[
        <Button key="cancel" onClick={handleCancel}>
          Cancel
        </Button>,
        <Button
          key="assign"
          type="primary"
          onClick={handleAssign}
          loading={assigningId !== null}
          disabled={!selectedConsultant || !selectedAvailabilityHour || !selectedDate || validationErr}
          style={{
            backgroundColor: selectedConsultant && selectedAvailabilityHour && selectedDate ? '#298d9f' : '#ccc',
            borderColor: selectedConsultant && selectedAvailabilityHour && selectedDate ? '#298d9f' : '#ccc',
          }}
        >
          Assign Consultant
        </Button>,
      ]}
    >
      <Spin spinning={loading}>
        <div className={`space-y-4 ${isMobile ? 'mb-4' : 'mb-6'}`}>
          {/* Search Input */}
          <Input
            placeholder="🔍 Search by consultant name"
            prefix={<SearchOutlined className="text-gray-400" />}
            value={searchTerm}
            onChange={(e) => handleSearch(e.target.value)}
            allowClear
            size={isMobile ? 'middle' : 'large'}
            className="rounded-xl border-gray-200 hover:border-blue-400 focus:border-blue-500 transition-colors duration-300"
          />

          {/* Working Day Filter */}
          <Select
            placeholder="📅 Filter by working day"
            allowClear
            value={filterWorkingDay}
            onChange={handleWorkingDayChange}
            size={isMobile ? 'middle' : 'large'}
            className="w-full rounded-xl"
          >
            {workingDays.map((day) => (
              <Select.Option key={day.value} value={day.value}>
                {day.label}
              </Select.Option>
            ))}
          </Select>
        </div>

        {/* Consultants List */}
        <div className={isMobile ? 'text-sm font-semibold mb-3' : 'text-base font-semibold mb-4'}>
          📋 Select Consultant ({totalCount} found)
        </div>
        {consultants.length > 0 ? (
          <div
            className={`${isMobile ? 'max-h-56' : 'max-h-64'
              } overflow-y-auto space-y-3 mb-4`}
          >
            {consultants.map((consultant) => (
              <Card
                key={consultant.doctorId}
                className={`cursor-pointer rounded-lg transition-all duration-300 border-2 ${selectedConsultant?.doctorId === consultant.doctorId
                    ? 'border-primary bg-blue-50 shadow-lg'
                    : 'border-gray-200 hover:border-blue-300 hover:shadow-md'
                  }`}
                onClick={() => handleConsultantSelect(consultant)}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    {/* Avatar */}
                    <div
                      className={`${isMobile ? 'w-10 h-10' : 'w-12 h-12'
                        } bg-gradient-to-r from-primary to-primary_hover rounded-full flex items-center justify-center text-white font-bold flex-shrink-0`}
                    >
                      {consultant.photoId ? (
                        <img
                          src={getImg(consultant.photoId)}
                          alt={consultant.name}
                          className="w-full h-full rounded-full object-cover"
                        />
                      ) : (
                        <UserOutlined />
                      )}
                    </div>

                    {/* Info */}
                    <div className="min-w-0 flex-1">
                      <h4
                        className={`font-semibold text-gray-800 ${isMobile ? 'text-sm' : 'text-base'
                          }`}
                      >
                        {consultant.name}
                      </h4>
                      {consultant.specialization && (
                        <p
                          className={`text-gray-600 ${isMobile ? 'text-xs' : 'text-sm'
                            }`}
                        >
                          {consultant.specialization}
                        </p>
                      )}
                      {consultant.clinics && consultant.clinics.length > 0 && (
                        <p
                          className={`text-gray-500 ${isMobile ? 'text-xs' : 'text-sm'
                            }`}
                        >
                          📍 {consultant.clinics.length} clinic(s) available
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Selection Indicator */}
                  <div className="flex-shrink-0 ml-2">
                    {selectedConsultant?.doctorId === consultant.doctorId && (
                      <div className="flex items-center justify-center w-6 h-6 bg-primary rounded-full">
                        <CheckOutlined className="text-white text-xs" />
                      </div>
                    )}
                  </div>
                </div>
              </Card>
            ))}
          </div>
        ) : (
          <Empty
            description={loading ? 'Loading consultants...' : 'No consultants found'}
            className="py-8 mb-4"
          />
        )}

        {/* Clinic Selection */}
        {selectedConsultant && availableClinics.length > 0 && (
          <>
            <Divider className="my-3 sm:my-4" />
            <div className={isMobile ? 'text-sm font-semibold mb-3' : 'text-base font-semibold mb-4'}>
              🏥 Select Clinic
            </div>
            <div className="space-y-2 mb-4">
              {availableClinics.map((clinic) => (
                <Card
                  key={clinic.clinicId}
                  className={`cursor-pointer rounded-lg transition-all duration-300 border-2 ${selectedClinic?.clinicId === clinic.clinicId
                      ? 'border-primary bg-blue-50'
                      : 'border-gray-200 hover:border-blue-300'
                    }`}
                  onClick={() => handleClinicSelect(clinic)}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <div className={`font-semibold text-gray-800 ${isMobile ? 'text-sm' : ''}`}>
                        <EnvironmentOutlined className="mr-2" />
                        {clinic.clinicName}
                      </div>
                      {clinic.detailedAddress && (
                        <div className={`text-gray-600 mt-1 ${isMobile ? 'text-xs' : 'text-sm'}`}>
                          {clinic.detailedAddress}
                        </div>
                      )}
                      {clinic.contactPhone && (
                        <div className={`text-gray-500 mt-1 ${isMobile ? 'text-xs' : 'text-sm'}`}>
                          📞 {clinic.contactPhone}
                        </div>
                      )}
                    </div>
                    {selectedClinic?.clinicId === clinic.clinicId && (
                      <CheckOutlined className="text-primary" />
                    )}
                  </div>
                </Card>
              ))}
            </div>
          </>
        )}

        {/* Working Day Selection */}
        {selectedClinic && availableWorkingDays.length > 0 && (
          <>
            <Divider className="my-3 sm:my-4" />
            <div className={isMobile ? 'text-sm font-semibold mb-3 flex items-center gap-1' : 'text-base font-semibold mb-4  flex items-center gap-1'}>
              <CalendarOutlined className="me-1" /> Select Working Day
            </div>
            <div className="space-y-2 mb-4">
              {availableWorkingDays.map((workingDay) => (
                <Card
                  key={workingDay.workingDayId}
                  className={`cursor-pointer rounded-lg transition-all duration-300 border-2 ${selectedWorkingDay?.workingDayId === workingDay.workingDayId
                      ? 'border-primary bg-blue-50'
                      : 'border-gray-200 hover:border-blue-300'
                    }`}
                  onClick={() => handleWorkingDaySelect(workingDay)}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <div className={`font-semibold text-gray-800 ${isMobile ? 'text-sm' : ''}`}>
                        {workingDays.find(d => d.value === workingDay.dayOfWeek)?.label || `Day ${workingDay.dayOfWeek}`}
                      </div>
                      <div className={`text-gray-600 mt-1 ${isMobile ? 'text-xs' : 'text-sm'}`}>
                        {workingDay.availabilityHours?.length || 0} time slot(s) available
                      </div>
                    </div>
                    {selectedWorkingDay?.workingDayId === workingDay.workingDayId && (
                      <CheckOutlined className="text-primary" />
                    )}
                  </div>
                </Card>
              ))}
            </div>
          </>
        )}

        {/* Availability Hours Selection */}
        {selectedWorkingDay && availabilityHours.length > 0 && (
          <>
            <Divider className="my-3 sm:my-4" />
            <div className={isMobile ? 'text-sm font-semibold mb-3' : 'text-base font-semibold mb-4'}>
              🕐 Select Time Slot
            </div>
            <div className="space-y-2 mb-4">
              <Radio.Group
                value={selectedAvailabilityHour?.availabilityHourId}
                onChange={(e) => {
                  const hour = availabilityHours.find((h) => h.availabilityHourId === e.target.value);
                  handleAvailabilityHourSelect(hour);
                }}
                style={{ width: '100%' }}
              >
                <Space direction="vertical" style={{ width: '100%' }}>
                  {availabilityHours.map((hour) => (
                    <Card
                      key={hour.availabilityHourId}
                      className={`cursor-pointer rounded-lg transition-all duration-300 border-2 ${selectedAvailabilityHour?.availabilityHourId === hour.availabilityHourId
                          ? 'border-primary bg-blue-50'
                          : 'border-gray-200 hover:border-gray-300'
                        }`}
                    >
                      <Radio value={hour.availabilityHourId} style={{ width: '100%' }}>
                        <div className="flex items-center justify-between flex-1 ml-2">
                          <div>
                            <div className={`font-semibold text-gray-800 ${isMobile ? 'text-sm' : ''}`}>
                              <ClockCircleOutlined className="mr-2" />
                              {dayjs(hour.startTime, 'HH:mm').format('hh:mm A')} -{' '}
                              {dayjs(hour.endTime, 'HH:mm').format('hh:mm A')}
                            </div>
                            <div className={`text-gray-600 mt-1 ${isMobile ? 'text-xs' : 'text-sm'}`}>
                              {hour.isHourly ? 'Hourly Sessions' : 'Full Period'}
                            </div>
                            {hour.maxPatients && (
                              <div className={`text-gray-500 mt-1 ${isMobile ? 'text-xs' : 'text-sm'}`}>
                                Max: {hour.maxPatients} {hour.maxPatientType || 'patients'}
                              </div>
                            )}
                          </div>
                          <Tag color="green" className={isMobile ? 'text-xs' : ''}>
                            Available
                          </Tag>
                        </div>
                      </Radio>
                    </Card>
                  ))}
                </Space>
              </Radio.Group>
            </div>
          </>
        )}
                {selectedClinic && availableWorkingDays.length <=0 && (
                <div className='font-semibold text-gray-700 text-lg flex gap-3 items-center'>
                 <InfoCircleOutlined/> No time available currently for this clinic
                </div>
                )}

        {/* Date Selection */}
        {selectedAvailabilityHour && selectedClinic && (
          <>
            <Divider className="my-3 sm:my-4" />
            <div className={isMobile ? 'text-sm font-semibold mb-3' : 'text-base font-semibold mb-4'}>
              <FaRegCalendarDays className="inline mr-2 text-primary" />
              Select Booking Date
            </div>

            {dateError && <Alert message={dateError} type="error" showIcon className="mb-4" />}

            {/* Calendar Container */}
            <div className="border border-textPrimary rounded-lg shadow-sm overflow-hidden max-w-xl mx-auto">
              {/* Calendar Header */}
              <div className="flex justify-between items-center p-3 bg-primary border-b border-textPrimary">
                <Button
                  type="text"
                  icon={<FaChevronLeft />}
                  className="text-white"
                  onClick={() => setCurrentMonth(currentMonth.subtract(1, 'month'))}
                />
                <div className="text-center">
                  <div className="text-lg font-semibold text-white">
                    {currentMonth.format('MMMM YYYY')}
                  </div>
                </div>
                <Button
                  type="text"
                  icon={<FaChevronRight />}
                  className="text-white"
                  onClick={() => setCurrentMonth(currentMonth.add(1, 'month'))}
                />
              </div>

              {/* Day Names Header */}
              <div className="grid grid-cols-7 bg-gray-50">
                {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map((day, index) => (
                  <div
                    key={index}
                    className="text-sm font-medium text-placeHolder text-center py-2 border border-textPrimary bg-cardColor"
                  >
                    {day}
                  </div>
                ))}
              </div>

              {/* Calendar Grid */}
              <div className="grid grid-cols-7 bg-white">
                {days.map((day, index) => {
                  if (!day) {
                    return (
                      <div
                        key={index}
                        className="h-12 border border-textPrimary bg-gray-50"
                      />
                    );
                  }

                  const isSelected = selectedDate && dayjs(selectedDate).isSame(day, 'day');
                  const isOtherMonth = !day.isSame(currentMonth, 'month');
                  const isDisabled = disabledDate(day);

                  return (
                    <div
                      key={index}
                      className={`h-12 flex items-center justify-center border cursor-pointer border-textPrimary
                        ${isSelected ? ' text-white' :
                          isDisabled ? 'bg-gray-50 text-gray-300 cursor-not-allowed' :
                            isOtherMonth ? 'bg-gray-50 text-gray-400' :
                              'hover:bg-page '}
                      `}
                      onClick={() => !isDisabled && !isOtherMonth && handleDateSelect(day)}
                    >
                      <div className={`flex items-center justify-center rounded-full w-8 h-8
                        ${isSelected ? 'bg-secondary text-white' : ''}`}>
                        {day.date()}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </>
        )}

        {/* Notes Input */}
        {selectedAvailabilityHour && selectedDate && (
          <>
            <Divider className="my-3 sm:my-4" />
            <div className={isMobile ? 'text-sm font-semibold mb-3' : 'text-base font-semibold mb-4'}>
              📝 Booking Notes *
            </div>
            <Input.TextArea
              onError={validationErr}
              rows={3}
              value={notes}
              onChange={(e) => { setNotes(e.target.value); e.target.value.length < 1 ? setValidationErr('Notes are required') : setValidationErr(''); }}
              placeholder="Add any notes for this booking..."
              className={`rounded-lg border-gray-200 focus:border-blue-500 transition-colors duration-300 ${validationErr ? 'border-red-500' : 'border-gray-200 hover:border-blue-300'}`}
            />
            <p className="text-red-500 text-sm">{validationErr}</p>


          </>
        )}

        {/* Selected Summary */}
        {selectedConsultant && selectedAvailabilityHour && selectedDate && (
          <div
            className={`mt-4 p-3 bg-green-50 border border-green-200 rounded-lg space-y-2 ${isMobile ? 'text-xs' : 'text-sm'
              }`}
          >
            <div className="font-semibold text-green-800">✅ Booking Summary</div>
            <div className="text-green-700">
              • <span className="font-medium">Consultant:</span> Dr. {selectedConsultant.name}
            </div>
            {selectedClinic && (
              <div className="text-green-700">
                • <span className="font-medium">Clinic:</span> {selectedClinic.clinicName}
              </div>
            )}
            {selectedWorkingDay && (
              <div className="text-green-700">
                • <span className="font-medium">Day:</span>{' '}
                {workingDays.find(d => d.value === selectedWorkingDay.dayOfWeek)?.label}
              </div>
            )}
            <div className="text-green-700">
              • <span className="font-medium">Time:</span> {selectedAvailabilityHour.startTime} -{' '}
              {selectedAvailabilityHour.endTime}
            </div>
            <div className="text-green-700">
              • <span className="font-medium">Booking Date:</span>{' '}
              {dayjs(selectedDate).format('MMM D, YYYY')}
            </div>
            {notes && (
              <div className="text-green-700">
                • <span className="font-medium">Notes:</span> {notes}
              </div>
            )}
          </div>
        )}

        {/* No Clinics Warning */}
        {selectedConsultant && availableClinics.length === 0 && (
          <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
            <div className={`text-yellow-800 ${isMobile ? 'text-xs' : 'text-sm'}`}>
              ⚠️ This consultant has no clinics configured. Please add clinic information first.
            </div>
          </div>
        )}
      </Spin>
    </Modal>
  );
};

export default ConsultantAssignmentModal;